import { PDFDocument } from "../types";
import { colors, margin } from "../constants";
import autoTable from "jspdf-autotable";
import {
  KulionerMerchant,
  KulionerOrder,
  KulionerProduct
} from "@/domain/entities/Kulioner";

const preloadImages = async (data: KulionerOrder): Promise<void> => {
  await Promise.all(
    data.merchants.map((merchant: KulionerMerchant) =>
      Promise.all(
        merchant.products.map((product: KulionerProduct) =>
          new Promise((resolve, reject) => {
            if (product.thumbnail) {
              const img = new Image();
              img.onload = resolve;
              img.onerror = reject;
              img.src = product.thumbnail;
            } else {
              resolve(null);
            }
          }).catch((error) => console.log(`Failed to preload image: ${error}`))
        )
      )
    )
  );
};

export const renderTable = async (
  doc: PDFDocument,
  startYTable: number,
  data: KulionerOrder,
): Promise<void> => {
  await preloadImages(data);

  let currentY = startYTable + 5;

 for (const merchant of data.merchants) {
  const boxStartX = 1;
  const boxWidth = 102;
  const padding = 2;

  const pageHeight = doc.internal.pageSize.getHeight();
  const bottomMargin = 15;

  // === Hitung tinggi yang dibutuhkan ===
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(6);
  const merchantNameHeight = doc.getTextDimensions(merchant.name).h;

  doc.setFont("Montserrat", "normal");
  const splitAddress = doc.splitTextToSize(merchant.address || "-", 80);
  const addressHeight = doc.getTextDimensions(splitAddress).h;

  
    let estimatedTableHeight = 0;

    merchant.products.forEach((product) => {
      const nameLines = doc.splitTextToSize(product.name, 60);
      const nameHeight = nameLines.length * 2;

      const totalHeight = Math.max(8, nameHeight + 4);
      estimatedTableHeight += totalHeight;
    });

    const totalNeededHeight =
      merchantNameHeight + addressHeight + estimatedTableHeight + 15;
    const spaceLeft = pageHeight - currentY - bottomMargin;

    // Page break hanya jika memang tidak cukup
    if (spaceLeft < totalNeededHeight) {
      doc.addPage();
      currentY = margin.top;
   }
  // === Merchant Name ===
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(6);
  doc.setTextColor("#333333");
  doc.text(merchant.name, boxStartX + padding, currentY);

  // === Address ===
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(6);
  doc.setTextColor("#A7A8AA");
  const addressY = currentY + merchantNameHeight + 2;
  doc.text(splitAddress, boxStartX + padding, addressY);

  const afterAddressY = addressY + addressHeight + 2;

  // === Table Body Generation (tetap) ===
  const productsTableBody: any[] = [];
  merchant.products.forEach((product, index) => {
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(6);
    const nameLines = doc.splitTextToSize(product.name, 60);
    const nameHeight = nameLines.length * 2;

    const totalHeight = Math.max(8, nameHeight + 4);
    productsTableBody.push([
      index + 1,
      { content: "", styles: { minCellHeight: totalHeight } },
      product.quantity
    ]);
  });

  // === Table Rendering ===
  autoTable(doc, {
    theme: "plain",
    head: [["No", "Produk", "Kuantitas"]],
    body: productsTableBody,
    columnStyles: {
      0: { cellWidth: 7 },
      1: { halign: "left" },
      2: { cellWidth: 13, halign: "center" }
    },
    styles: {
      font: "Montserrat",
      fontSize: 6,
      fontStyle: "bold",
      textColor: "#333333",
      valign: "middle",
      halign: "left",
      cellPadding: { top: 1, bottom: 1, left: 1, right: 1 }
    },
    headStyles: {
      fillColor: "#F2F4F5",
      fontSize: 6,
      fontStyle: "bold",
      textColor: "#333333",
      minCellHeight: 5,
      cellPadding: 1,
      lineWidth: 0.1,
      lineColor: "#EBEBEB"
    },
    tableWidth: boxWidth - padding * 2,
    startY: afterAddressY,
    margin: {
      left: boxStartX + padding,
      right: margin.right,
    },
    pageBreak: "avoid",
    willDrawCell: (table) => {
      if (!merchant.products[table.row.index]) return;
      if (table.section === "body") {
        doc.setDrawColor("#EBEBEB");
        doc.setLineWidth(0.2);
        doc.line(
          table.cell.x,
          table.cell.y + table.cell.height,
          table.cell.x + table.cell.width,
          table.cell.y + table.cell.height
        );
      }
    },
    didDrawCell: (data) => {
      const { column, row, cell } = data;
      if (data.section === "body" && column.index === 1) {
        if (!merchant.products[row.index]) return;
        const product = merchant.products[row.index];
        const textX = cell.x + 1.5;
        let textY = cell.y + 4;

        doc.setFont("Montserrat", "bold");
        doc.setFontSize(6);
        doc.setTextColor(colors.primary);
        const nameLines = doc.splitTextToSize(product.name, column.width - 3);
        const maxLines = 2;
        const displayLines = nameLines.slice(0, maxLines);
        const needsEllipsis = nameLines.length > maxLines;

        displayLines.forEach((line: string, index: number) => {
          if (index === maxLines - 1 && needsEllipsis) {
            // Add ellipsis to last line if truncated
            const truncatedLine = line.length > 3 ? line.slice(0, -3) + '...' : line + '...';
            doc.text(truncatedLine, textX, textY);
          } else {
            doc.text(line, textX, textY);
          }
          textY += 2;
        });
      }
    },
  });

  const tableEndY = doc.lastAutoTable?.finalY || afterAddressY;

  // === Total Quantity ===
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(6);
  doc.setTextColor("#333333");
  doc.text("Total kuantitas", boxStartX + boxWidth - 2, tableEndY + 4, {
    align: "right",
  });

  doc.setTextColor("#B82025");
  doc.text(`${merchant.totalQuantity}`, boxStartX + boxWidth - 2, tableEndY + 8, {
    align: "right",
  });

  currentY = tableEndY + 12 + margin.top;
}

};
